import React, { useEffect, useMemo, useState } from 'react';
import { toast } from 'react-hot-toast';
import inventoryApi from '../../../services/inventoryApi';
import SearchableInventorySupplierDropdown from '../../Common/SearchableInventorySupplierDropdown';
import SearchableLedgerDropdown from '../../Common/SearchableLedgerDropdown';
import LoadingSpinner from '../../Common/LoadingSpinner';
import useClosedPeriod from '../../../hooks/useClosedPeriod';

/**
 * From GRNs — pick GRNs / service completions (any supplier), apply one rule and create one debit note
 * per supplier covering its selected GRNs. Preview first; nothing is saved while a note has a problem.
 *   Adjustment: % off the price, or an amount off each unit (reverses the cost, budget given back)
 *   Penalty:    fixed amount per GRN, or % of each GRN's open value (to penalty income)
 *   Rebate:     % of open value, or fixed amount per GRN (to discount received)
 */
const RULES = {
  ADJUSTMENT: { label: 'Price / Quantity Adjustment', bases: [['PERCENT', '% off the GRN price'], ['PER_UNIT', 'Amount off each unit']] },
  PENALTY: { label: 'Penalty / Liquidated Damages', bases: [['PER_GRN', 'Fixed amount per GRN'], ['PERCENT', '% of each GRN\'s open value']] },
  REBATE: { label: 'Rebate / Discount', bases: [['PERCENT', '% of open value'], ['PER_GRN', 'Fixed amount per GRN']] }
};
const fmt = (n) => (Number(n) || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const daysAgo = (n) => { const d = new Date(); d.setDate(d.getDate() - n); return d.toISOString().split('T')[0]; };

const DebitNoteBulkFromGRNs = ({ onClose, onSuccess }) => {
  // filters
  const [supplier, setSupplier] = useState('');
  const [supplierLabel, setSupplierLabel] = useState('');
  const [from, setFrom] = useState(daysAgo(90));
  const [to, setTo] = useState(new Date().toISOString().split('T')[0]);
  const [procurementType, setProcurementType] = useState('');
  const [search, setSearch] = useState('');
  const [grns, setGrns] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(new Set());
  // rule
  const [noteType, setNoteType] = useState('REBATE');
  const [basis, setBasis] = useState('PERCENT');
  const [value, setValue] = useState('');
  const [taxRate, setTaxRate] = useState('');
  const [description, setDescription] = useState('');
  const [ledger, setLedger] = useState({ id: '', label: '' });
  const [defaults, setDefaults] = useState({});
  const [debitDate, setDebitDate] = useState(new Date().toISOString().split('T')[0]);
  // preview / result
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);

  const { minDate, dateError } = useClosedPeriod();

  const closedError = dateError(debitDate);


  useEffect(() => {
    document.body.style.overflow = 'hidden';
    inventoryApi.getDebitNoteChargeDefaults().then(res => setDefaults(res.data || {})).catch(() => {});
    return () => { document.body.style.overflow = 'unset'; };
  }, []);

  // the rule's account follows the type's default until the user picks one
  useEffect(() => {
    if (noteType === 'ADJUSTMENT') return;
    const d = defaults[noteType];
    setLedger(prev => (prev.picked ? prev : { id: d?.accountId || '', label: d?.label || '' }));
  }, [noteType, defaults]);

  const load = async () => {
    setLoading(true);
    setPreview(null);
    try {
      const res = await inventoryApi.getOpenGRNsForDebitNotes({ supplierId: supplier || undefined, from, to, procurementType: procurementType || undefined, search: search || undefined });
      setGrns(res.data || []);
      setSelected(new Set());
      if (!(res.data || []).length) toast('No GRNs with an open balance for these filters');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to load GRNs');
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); /* first load: last 90 days */ // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const toggle = (id) => { setPreview(null); setSelected(prev => { const n = new Set(prev); if (n.has(id)) n.delete(id); else n.add(id); return n; }); };
  const allSelected = grns.length > 0 && grns.every(g => selected.has(g._id));
  const toggleAll = () => { setPreview(null); setSelected(allSelected ? new Set() : new Set(grns.map(g => g._id))); };

  const chosen = grns.filter(g => selected.has(g._id));
  const suppliersChosen = useMemo(() => new Set(chosen.map(g => String(g.supplier))).size, [chosen]);
  const chosenValue = chosen.reduce((t, g) => t + (g.openValue || 0), 0);

  const changeType = (t) => { setNoteType(t); setBasis(RULES[t].bases[0][0]); setPreview(null); if (t === 'PENALTY') setTaxRate(''); };

  const body = (dryRun) => ({
    grnIds: chosen.map(g => g._id),
    debitDate,
    dryRun,
    rule: {
      noteType, basis,
      value: parseFloat(value) || 0,
      taxRate: noteType === 'PENALTY' ? 0 : (parseFloat(taxRate) || 0),
      description: description.trim(),
      ...(noteType !== 'ADJUSTMENT' && ledger.id && { ledgerAccount: { accountId: ledger.id, label: ledger.label } })
    }
  });

  const check = () => {
    if (!chosen.length) { toast.error('Select at least one GRN'); return false; }
    if (!((parseFloat(value) || 0) > 0)) { toast.error(basis === 'PERCENT' ? 'Enter the percentage' : 'Enter the amount'); return false; }
    if (basis === 'PERCENT' && parseFloat(value) > 100) { toast.error('The percentage cannot be more than 100'); return false; }
    if (noteType !== 'ADJUSTMENT' && !ledger.id) { toast.error('Choose the account to credit'); return false; }
    return true;
  };

  const runPreview = async () => {
    if (!check()) return;
    setBusy(true);
    try {
      const res = await inventoryApi.bulkDebitNotesFromGRNs(body(true));
      setPreview(res.data?.notes || []);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Preview failed');
    } finally {
      setBusy(false);
    }
  };

  const create = async () => {
    if (!check()) return;
    setBusy(true);
    try {
      const res = await inventoryApi.bulkDebitNotesFromGRNs(body(false));
      toast.success(res.message || 'Debit notes created');
      onSuccess?.(res.data);
    } catch (err) {
      if (err.response?.data?.data?.notes) setPreview(err.response.data.data.notes);
      toast.error(err.response?.data?.message || 'Failed to create the debit notes');
    } finally {
      setBusy(false);
    }
  };

  const problems = (preview || []).filter(p => p.errors?.length).length;
  const input = 'w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-amber-500';
  const unitHint = basis === 'PERCENT' ? '%' : noteType === 'ADJUSTMENT' ? 'per unit' : 'per GRN';

  return (
    <div className="fixed inset-0 backdrop-blur-sm bg-black/40 z-50 flex items-center justify-center p-4">
      <div className="relative w-full max-w-7xl max-h-[94vh] bg-white shadow-2xl rounded-2xl flex flex-col overflow-hidden">
        <div className="flex-shrink-0 bg-gradient-to-r from-amber-600 to-orange-600 px-6 py-3 flex items-center justify-between">
          <div>
            <h3 className="text-xl font-bold text-white">Bulk Debit Notes — From GRNs</h3>
            <p className="text-xs text-amber-100">Select GRNs / service completions, apply one rule · one debit note per supplier · all pending approval</p>
          </div>
          <button onClick={onClose} className="w-9 h-9 rounded-lg flex items-center justify-center text-white/80 hover:text-white hover:bg-white/20">✕</button>
        </div>

        <div className="flex-1 overflow-y-auto p-4 space-y-3">
          {/* 1. Filters */}
          <div className="grid grid-cols-2 md:grid-cols-6 gap-2 items-end">
            <div className="col-span-2">
              <label className="block text-xs font-semibold text-gray-600 mb-1">Supplier</label>
              <SearchableInventorySupplierDropdown value={supplier} displayLabel={supplierLabel}
                onChange={(val, label) => { setSupplier(val || ''); setSupplierLabel(label || ''); }} placeholder="All suppliers" menuPlacement="down" />
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-600 mb-1">Received from</label>
              <input type="date" value={from} onChange={(e) => setFrom(e.target.value)} className={input} />
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-600 mb-1">to</label>
              <input type="date" value={to} onChange={(e) => setTo(e.target.value)} className={input} />
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-600 mb-1">Type</label>
              <select value={procurementType} onChange={(e) => setProcurementType(e.target.value)} className={input}>
                <option value="">All</option>
                <option value="INVENTORY">Inventory</option>
                <option value="FIXED_ASSET">Fixed asset</option>
                <option value="SERVICE">Service</option>
              </select>
            </div>
            <div className="flex gap-2">
              <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="GRN no." className={input} onKeyDown={(e) => e.key === 'Enter' && load()} />
              <button onClick={load} disabled={loading} className="px-3 py-1.5 text-sm font-medium text-white bg-amber-600 hover:bg-amber-700 rounded disabled:opacity-50">Load</button>
            </div>
          </div>

          {/* 2. GRNs */}
          <div className="border border-gray-200 rounded-lg overflow-hidden">
            <div className="bg-gray-100 px-3 py-1.5 text-xs text-gray-700 flex justify-between">
              <span className="font-semibold">GRNs with an open balance ({grns.length})</span>
              <span>Selected: <b>{chosen.length}</b> GRN(s) · <b>{suppliersChosen}</b> supplier(s) · open value <b>{fmt(chosenValue)}</b></span>
            </div>
            <div className="max-h-72 overflow-y-auto">
              {loading ? <div className="py-6 flex justify-center"><LoadingSpinner /></div> : (
                <table className="w-full text-sm" data-testid="bulk-grn-table">
                  <thead className="bg-white text-xs text-gray-500 sticky top-0">
                    <tr>
                      <th className="px-2 py-1.5 w-8"><input type="checkbox" checked={allSelected} onChange={toggleAll} aria-label="Select all" /></th>
                      <th className="px-2 py-1.5 text-left">GRN</th>
                      <th className="px-2 py-1.5 text-left">Received</th>
                      <th className="px-2 py-1.5 text-left">Supplier</th>
                      <th className="px-2 py-1.5 text-left">Type</th>
                      <th className="px-2 py-1.5 text-left">PO</th>
                      <th className="px-2 py-1.5 text-right">Open lines</th>
                      <th className="px-2 py-1.5 text-right">Open value</th>
                    </tr>
                  </thead>
                  <tbody>
                    {grns.map(g => (
                      <tr key={g._id} className={`border-t border-gray-100 cursor-pointer ${selected.has(g._id) ? 'bg-amber-50' : 'hover:bg-gray-50'}`} onClick={() => toggle(g._id)}>
                        <td className="px-2 py-1"><input type="checkbox" checked={selected.has(g._id)} onChange={() => toggle(g._id)} onClick={(e) => e.stopPropagation()} /></td>
                        <td className="px-2 py-1 font-medium text-gray-800">{g.grnNumber}</td>
                        <td className="px-2 py-1 text-gray-600">{g.receiptDate ? new Date(g.receiptDate).toLocaleDateString() : '—'}</td>
                        <td className="px-2 py-1 text-gray-700">{g.supplierName}</td>
                        <td className="px-2 py-1 text-xs text-gray-500">{g.procurementType === 'SERVICE' ? 'Service' : g.procurementType === 'FIXED_ASSET' ? 'Fixed asset' : 'Inventory'}</td>
                        <td className="px-2 py-1 text-xs text-gray-500">{g.purchaseOrderNumber || '—'}</td>
                        <td className="px-2 py-1 text-right text-gray-600">{g.openLines}</td>
                        <td className="px-2 py-1 text-right font-medium">{fmt(g.openValue)}</td>
                      </tr>
                    ))}
                    {!grns.length && <tr><td colSpan={8} className="px-3 py-6 text-center text-sm text-gray-500">No GRNs — change the filters and Load</td></tr>}
                  </tbody>
                </table>
              )}
            </div>
          </div>

          {/* 3. Rule */}
          <div className="border border-amber-200 bg-amber-50/40 rounded-lg p-3 space-y-2">
            <div className="text-sm font-semibold text-gray-800">Rule</div>
            <div className="grid grid-cols-2 md:grid-cols-6 gap-2 items-end">
              <div className="col-span-2">
                <label className="block text-xs font-semibold text-gray-600 mb-1">Debit note type</label>
                <select value={noteType} onChange={(e) => changeType(e.target.value)} className={input} data-testid="rule-type">
                  {Object.entries(RULES).map(([k, r]) => <option key={k} value={k}>{r.label}</option>)}
                </select>
              </div>
              <div className="col-span-2">
                <label className="block text-xs font-semibold text-gray-600 mb-1">Calculate as</label>
                <select value={basis} onChange={(e) => { setBasis(e.target.value); setPreview(null); }} className={input} data-testid="rule-basis">
                  {RULES[noteType].bases.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-1">Value ({unitHint})</label>
                <input type="number" min="0" step="0.01" value={value} onChange={(e) => { setValue(e.target.value); setPreview(null); }} className={`${input} text-right`} data-testid="rule-value" />
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-1">Tax % {noteType === 'PENALTY' ? '(none)' : '(VAT reversed)'}</label>
                <input type="number" min="0" max="100" step="0.01" value={noteType === 'PENALTY' ? '' : taxRate} disabled={noteType === 'PENALTY'}
                  onChange={(e) => { setTaxRate(e.target.value); setPreview(null); }} className={`${input} text-right disabled:bg-gray-100`} />
              </div>
              <div className="col-span-2">
                <label className="block text-xs font-semibold text-gray-600 mb-1">Description</label>
                <input value={description} onChange={(e) => { setDescription(e.target.value); setPreview(null); }} className={input}
                  placeholder={noteType === 'PENALTY' ? 'Late delivery penalty' : noteType === 'REBATE' ? 'Volume rebate Q3' : 'Agreed price reduction'} />
              </div>
              {noteType !== 'ADJUSTMENT' ? (
                <div className="col-span-2">
                  <label className="block text-xs font-semibold text-gray-600 mb-1">Credit account</label>
                  <SearchableLedgerDropdown value={ledger.id} displayLabel={ledger.label}
                    onChange={(id, lbl) => { setLedger({ id: id || '', label: lbl || '', picked: true }); setPreview(null); }}
                    ledgerGroup={['REVENUE', 'EXPENSES']} placeholder="Select account…" menuPlacement="up" />
                </div>
              ) : (
                <p className="col-span-2 text-xs text-gray-500 self-center">Credits the stock / asset / service expense account each GRN debited, and gives the budget back on approval.</p>
              )}
              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-1">Debit date</label>
                <input type="date" value={debitDate} min={minDate} onChange={(e) => setDebitDate(e.target.value)} className={input} />
              {closedError && <p className="text-xs text-red-600 mt-0.5">{closedError}</p>}
              </div>
            </div>
          </div>

          {/* 4. Preview */}
          {preview && (
            <div className="border border-gray-200 rounded-lg overflow-x-auto" data-testid="bulk-preview">
              <div className="bg-gray-100 px-3 py-1.5 text-xs font-semibold text-gray-700">
                Preview — {preview.length} debit note(s){problems ? <span className="text-red-600"> · {problems} with problems (fix or untick those GRNs)</span> : ''}
              </div>
              <table className="w-full text-sm">
                <thead className="text-xs text-gray-500 bg-white">
                  <tr>
                    <th className="px-2 py-1.5 text-left">Supplier</th>
                    <th className="px-2 py-1.5 text-left">GRNs</th>
                    <th className="px-2 py-1.5 text-right">Lines</th>
                    <th className="px-2 py-1.5 text-right">Amount</th>
                    <th className="px-2 py-1.5 text-right">Tax</th>
                    <th className="px-2 py-1.5 text-right">Total</th>
                    <th className="px-2 py-1.5 text-left">Note</th>
                  </tr>
                </thead>
                <tbody>
                  {preview.map(p => (
                    <tr key={p.supplier} className={`border-t border-gray-100 align-top ${p.errors?.length ? 'bg-red-50' : ''}`}>
                      <td className="px-2 py-1.5 font-medium text-gray-800">{p.supplierName}</td>
                      <td className="px-2 py-1.5 text-xs text-gray-600">{(p.grnNumbers || []).join(', ')}
                        {(p.skipped || []).length > 0 && <div className="text-amber-700">{p.skipped.join('; ')}</div>}
                      </td>
                      <td className="px-2 py-1.5 text-right">{p.lines}</td>
                      <td className="px-2 py-1.5 text-right">{fmt(p.subtotal)}</td>
                      <td className="px-2 py-1.5 text-right">{fmt(p.taxAmount)}</td>
                      <td className="px-2 py-1.5 text-right font-semibold">{fmt(p.total)}</td>
                      <td className="px-2 py-1.5 text-xs">
                        {p.debitNoteNumber ? <span className="text-green-700">{p.debitNoteNumber}</span>
                          : p.errors?.length ? <span className="text-red-600">{p.errors.join('; ')}</span> : <span className="text-gray-500">ready</span>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        <div className="flex-shrink-0 bg-gray-50 px-5 py-3 border-t border-gray-200 flex items-center justify-between gap-3">
          <span className="text-xs text-gray-500">Preview first — nothing is saved while any supplier's note has a problem.</span>
          <div className="flex gap-3">
            <button onClick={onClose} className="px-4 py-1.5 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 bg-white hover:bg-gray-50">Cancel</button>
            <button onClick={runPreview} disabled={busy || !chosen.length}
              className="px-4 py-1.5 border border-amber-500 rounded-lg text-sm font-medium text-amber-700 bg-white hover:bg-amber-50 disabled:opacity-50">
              {busy && !preview ? 'Working…' : 'Preview'}
            </button>
            <button onClick={create} disabled={busy || !preview || problems > 0 || !!closedError}
              title={!preview ? 'Preview first' : problems ? 'Fix the notes with problems first' : ''}
              className="px-4 py-1.5 bg-gradient-to-r from-amber-600 to-orange-600 hover:from-amber-700 hover:to-orange-700 text-white text-sm font-medium rounded-lg disabled:opacity-50">
              {busy && preview ? 'Creating…' : `Create ${preview ? preview.length : ''} Debit Note(s)`}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DebitNoteBulkFromGRNs;
